import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  BarChart2,
  UserCheck,
  Users,
  TrendingDown,
  Eye,
  EyeOff,
  Calendar,
  User,
  AlertCircle,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';

const ROUTE_METHODS = [
  { value: 'round_robin', label: 'Round Robin', icon: RefreshCw, color: theme.colors.info },
  { value: 'weighted', label: 'Weighted', icon: BarChart2, color: '#A855F7' },
  { value: 'specific_agent', label: 'Specific Agent', icon: UserCheck, color: theme.colors.success },
  { value: 'pond', label: 'Pond', icon: Users, color: theme.colors.accent },
  { value: 'least_active', label: 'Least Active', icon: TrendingDown, color: '#06B6D4' },
];

const MATCH_SOURCES = [
  'zillow',
  'realtor_com',
  'facebook',
  'email',
  'trulia',
  'redfin',
  'walk_in',
  'manual',
  'bright_mls',
  'wix_website',
  'homes_com',
];

const MATCH_TYPES = ['buyer', 'seller', 'renter', 'commercial', 'investor', 'pm_owner'];

// Demo data for demoMode
const DEMO_RULES = [
  {
    id: '1',
    name: 'Zillow Buyer Leads',
    route_method: 'round_robin',
    agent_pool: [{ id: 'a1', full_name: 'Alice Johnson' }, { id: 'a2', full_name: 'Bob Smith' }],
    match_source: ['zillow'],
    match_type: ['buyer'],
    match_zip: ['07001', '07002', '07003'],
    match_tags: [],
    auto_tags: ['zillow', 'buyer'],
    notify_agent: true,
    is_active: true,
    respect_capacity: true,
    max_active_leads: 20,
    skip_inactive: true,
    priority: 1,
  },
  {
    id: '2',
    name: 'Facebook Seller Leads',
    route_method: 'weighted',
    agent_pool: [{ id: 'a3', full_name: 'Carol White' }],
    match_source: ['facebook'],
    match_type: ['seller'],
    match_zip: [],
    match_tags: [],
    auto_tags: ['facebook', 'seller'],
    notify_agent: true,
    is_active: true,
    respect_capacity: false,
    max_active_leads: null,
    skip_inactive: true,
    priority: 2,
  },
];

const DEMO_LOG = [
  {
    id: 'log1',
    created_at: new Date(Date.now() - 300000).toISOString(),
    contact: { id: 'c1', first_name: 'John', last_name: 'Doe' },
    rule: { id: '1', name: 'Zillow Buyer Leads' },
    assigned_to: { id: 'a1', full_name: 'Alice Johnson' },
    method: 'round_robin',
    claimed_by: { id: 'a1', full_name: 'Alice Johnson' },
  },
  {
    id: 'log2',
    created_at: new Date(Date.now() - 600000).toISOString(),
    contact: { id: 'c2', first_name: 'Jane', last_name: 'Smith' },
    rule: { id: '2', name: 'Facebook Seller Leads' },
    assigned_to: { id: 'a3', full_name: 'Carol White' },
    method: 'weighted',
    claimed_by: null,
  },
];

function RuleEditorModal({ rule, agents, onSave, onCancel }) {
  const [form, setForm] = useState(
    rule || {
      name: '',
      route_method: 'round_robin',
      agent_pool: [],
      match_source: [],
      match_type: [],
      match_zip: '',
      match_tags: '',
      auto_tags: '',
      notify_agent: true,
      respect_capacity: false,
      max_active_leads: 20,
      skip_inactive: true,
    }
  );

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAgentToggle = (agentId) => {
    setForm((prev) => ({
      ...prev,
      agent_pool: prev.agent_pool.some((a) => (a.id || a) === agentId)
        ? prev.agent_pool.filter((a) => (a.id || a) !== agentId)
        : [...prev.agent_pool, agents.find((a) => a.id === agentId)],
    }));
  };

  const handleSourceToggle = (source) => {
    setForm((prev) => ({
      ...prev,
      match_source: prev.match_source.includes(source)
        ? prev.match_source.filter((s) => s !== source)
        : [...prev.match_source, source],
    }));
  };

  const handleTypeToggle = (type) => {
    setForm((prev) => ({
      ...prev,
      match_type: prev.match_type.includes(type)
        ? prev.match_type.filter((t) => t !== type)
        : [...prev.match_type, type],
    }));
  };

  const handleSave = () => {
    const payload = {
      ...form,
      match_zip: form.match_zip ? form.match_zip.split(',').map((z) => z.trim()) : [],
      match_tags: form.match_tags ? form.match_tags.split(',').map((t) => t.trim()) : [],
      auto_tags: form.auto_tags ? form.auto_tags.split(',').map((t) => t.trim()) : [],
    };
    onSave(payload);
  };

  const selectedAgentIds = new Set(form.agent_pool.map((a) => a.id || a));

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: theme.colors.card,
          borderRadius: theme.radius.lg,
          padding: '24px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: theme.shadow.lg,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: '0 0 20px 0', color: theme.colors.text, fontSize: '18px', fontWeight: '600' }}>
          {rule ? 'Edit Routing Rule' : 'New Routing Rule'}
        </h2>

        {/* Name */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: theme.colors.text }}>
            Rule Name
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g. Zillow Buyer Leads"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Route Method */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: theme.colors.text }}>
            Route Method
          </label>
          <select
            value={form.route_method}
            onChange={(e) => handleChange('route_method', e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              boxSizing: 'border-box',
            }}
          >
            {ROUTE_METHODS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* Agent Pool */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: theme.colors.text }}>
            Agent Pool
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {agents.map((agent) => (
              <label key={agent.id} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={selectedAgentIds.has(agent.id)}
                  onChange={() => handleAgentToggle(agent.id)}
                  style={{ marginRight: '8px', cursor: 'pointer' }}
                />
                {agent.full_name}
              </label>
            ))}
          </div>
        </div>

        {/* Match Sources */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: theme.colors.text }}>
            Match Source
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {MATCH_SOURCES.map((source) => (
              <label key={source} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  checked={form.match_source.includes(source)}
                  onChange={() => handleSourceToggle(source)}
                  style={{ marginRight: '6px', cursor: 'pointer' }}
                />
                {source.replace(/_/g, ' ')}
              </label>
            ))}
          </div>
        </div>

        {/* Match Types */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: theme.colors.text }}>
            Match Type
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {MATCH_TYPES.map((type) => (
              <label key={type} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  checked={form.match_type.includes(type)}
                  onChange={() => handleTypeToggle(type)}
                  style={{ marginRight: '6px', cursor: 'pointer' }}
                />
                {type.replace(/_/g, ' ')}
              </label>
            ))}
          </div>
        </div>

        {/* Zip Codes */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: theme.colors.text }}>
            Zip Codes (comma-separated)
          </label>
          <input
            type="text"
            value={form.match_zip}
            onChange={(e) => handleChange('match_zip', e.target.value)}
            placeholder="07001, 07002, 07003"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Tags */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: theme.colors.text }}>
            Match Tags (comma-separated)
          </label>
          <input
            type="text"
            value={form.match_tags}
            onChange={(e) => handleChange('match_tags', e.target.value)}
            placeholder="hot, qualified"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Auto Tags */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: theme.colors.text }}>
            Auto Tags (comma-separated)
          </label>
          <input
            type="text"
            value={form.auto_tags}
            onChange={(e) => handleChange('auto_tags', e.target.value)}
            placeholder="auto-tag1, auto-tag2"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Toggles */}
        <div style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px' }}>
            <input
              type="checkbox"
              checked={form.notify_agent}
              onChange={(e) => handleChange('notify_agent', e.target.checked)}
              style={{ marginRight: '8px', cursor: 'pointer' }}
            />
            Notify Agent
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px' }}>
            <input
              type="checkbox"
              checked={form.skip_inactive}
              onChange={(e) => handleChange('skip_inactive', e.target.checked)}
              style={{ marginRight: '8px', cursor: 'pointer' }}
            />
            Skip Inactive Agents
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px' }}>
            <input
              type="checkbox"
              checked={form.respect_capacity}
              onChange={(e) => handleChange('respect_capacity', e.target.checked)}
              style={{ marginRight: '8px', cursor: 'pointer' }}
            />
            Respect Capacity
          </label>
        </div>

        {/* Max Active Leads (shown only if respect_capacity) */}
        {form.respect_capacity && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: theme.colors.text }}>
              Max Active Leads
            </label>
            <input
              type="number"
              value={form.max_active_leads || ''}
              onChange={(e) => handleChange('max_active_leads', parseInt(e.target.value, 10) || null)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.radius.sm,
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              border: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.card,
              color: theme.colors.text,
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = '#F0F4F8')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = theme.colors.card)}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '8px 16px',
              backgroundColor: theme.colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = theme.colors.primaryLight)}
            onMouseLeave={(e) => (e.target.style.backgroundColor = theme.colors.primary)}
          >
            Save Rule
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LeadRoutingTab({ demoMode, showToast, agents }) {
  const [rules, setRules] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [loadingLog, setLoadingLog] = useState(false);

  // Load rules
  const loadRules = useCallback(async () => {
    if (demoMode) {
      setRules(DEMO_RULES);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const res = await fetch('/api/lead-routing-rules', {
        headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
      });
      const data = await res.json();
      setRules(data.rules || []);
    } catch (err) {
      console.error('Error loading rules:', err);
      showToast('Failed to load routing rules', 'error');
    } finally {
      setLoading(false);
    }
  }, [demoMode, showToast]);

  // Load log
  const loadLog = useCallback(async () => {
    if (demoMode) {
      setLog(DEMO_LOG);
      setLoadingLog(false);
      return;
    }

    try {
      setLoadingLog(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const res = await fetch('/api/lead-routing-rules?log=1&limit=50', {
        headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
      });
      const data = await res.json();
      setLog(data.log || []);
    } catch (err) {
      console.error('Error loading log:', err);
      showToast('Failed to load routing log', 'error');
    } finally {
      setLoadingLog(false);
    }
  }, [demoMode, showToast]);

  // Initial load
  useEffect(() => {
    loadRules();
  }, []);

  // Save rule
  const handleSaveRule = async (payload) => {
    if (!payload.name.trim()) {
      showToast('Rule name is required', 'error');
      return;
    }

    if (payload.agent_pool.length === 0) {
      showToast('Select at least one agent', 'error');
      return;
    }

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const method = editingRule ? 'PATCH' : 'POST';
      const url = editingRule ? `/api/lead-routing-rules?id=${editingRule.id}` : '/api/lead-routing-rules';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Failed to save rule');

      showToast(editingRule ? 'Rule updated' : 'Rule created', 'success');
      setShowEditor(false);
      setEditingRule(null);
      loadRules();
    } catch (err) {
      console.error('Error saving rule:', err);
      showToast('Failed to save rule', 'error');
    }
  };

  // Delete rule
  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Delete this routing rule?')) return;

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const res = await fetch(`/api/lead-routing-rules?id=${ruleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
      });

      if (!res.ok) throw new Error('Failed to delete rule');

      showToast('Rule deleted', 'success');
      loadRules();
    } catch (err) {
      console.error('Error deleting rule:', err);
      showToast('Failed to delete rule', 'error');
    }
  };

  // Toggle active
  const handleToggleActive = async (ruleId, isActive) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const res = await fetch(`/api/lead-routing-rules?id=${ruleId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
        body: JSON.stringify({ is_active: !isActive }),
      });

      if (!res.ok) throw new Error('Failed to toggle rule');

      loadRules();
    } catch (err) {
      console.error('Error toggling rule:', err);
      showToast('Failed to toggle rule', 'error');
    }
  };

  // Get method info
  const getMethodInfo = (method) => {
    return ROUTE_METHODS.find((m) => m.value === method);
  };

  // Format condition summary
  const formatConditionSummary = (rule) => {
    const parts = [];
    if (rule.match_source?.length) parts.push(`${rule.match_source.length} source(s)`);
    if (rule.match_type?.length) parts.push(`${rule.match_type.length} type(s)`);
    if (rule.match_zip?.length) parts.push(`${rule.match_zip.length} zip(s)`);
    if (rule.match_tags?.length) parts.push(`${rule.match_tags.length} tag(s)`);
    return parts.length > 0 ? parts.join(' • ') : 'No conditions';
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: theme.colors.textLight }}>
        Loading routing rules...
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0, color: theme.colors.text, fontSize: '20px', fontWeight: '600' }}>
          Routing Rules
        </h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => {
              setShowLog(!showLog);
              if (!showLog) loadLog();
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: showLog ? theme.colors.primaryLight : theme.colors.bg,
              color: showLog ? 'white' : theme.colors.text,
              border: 'none',
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {showLog ? <EyeOff size={16} /> : <Eye size={16} />}
            {showLog ? 'Hide Log' : 'View Log'}
          </button>
          <button
            onClick={() => {
              setEditingRule(null);
              setShowEditor(true);
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: theme.colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = theme.colors.primaryLight)}
            onMouseLeave={(e) => (e.target.style.backgroundColor = theme.colors.primary)}
          >
            <Plus size={16} />
            New Rule
          </button>
        </div>
      </div>

      {/* Rules List */}
      {!showLog ? (
        rules.length === 0 ? (
          <div
            style={{
              backgroundColor: theme.colors.card,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.radius.md,
              padding: '40px 24px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <AlertCircle size={32} color={theme.colors.textLight} />
            <p style={{ color: theme.colors.textLight, fontSize: '14px', margin: 0 }}>
              No routing rules yet. All new leads go to the Lead Inbox for manual assignment.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {rules
              .sort((a, b) => (a.priority || 999) - (b.priority || 999))
              .map((rule) => {
                const methodInfo = getMethodInfo(rule.route_method);
                const MethodIcon = methodInfo?.icon || Users;

                return (
                  <div
                    key={rule.id}
                    style={{
                      backgroundColor: theme.colors.card,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.radius.md,
                      padding: '16px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      transition: 'box-shadow 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.boxShadow = theme.shadow.md)}
                    onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <h3 style={{ margin: 0, color: theme.colors.text, fontSize: '15px', fontWeight: '600' }}>
                          {rule.name}
                        </h3>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 8px',
                            backgroundColor: methodInfo?.color || theme.colors.bg,
                            color: 'white',
                            borderRadius: theme.radius.sm,
                            fontSize: '12px',
                            fontWeight: '500',
                          }}
                        >
                          <MethodIcon size={14} />
                          {methodInfo?.label}
                        </span>
                        {!rule.is_active && (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              backgroundColor: '#FEE2E2',
                              color: theme.colors.danger,
                              borderRadius: theme.radius.sm,
                              fontSize: '11px',
                              fontWeight: '600',
                            }}
                          >
                            INACTIVE
                          </span>
                        )}
                      </div>
                      <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: theme.colors.textLight }}>
                        {formatConditionSummary(rule)}
                      </p>
                      <p style={{ margin: 0, fontSize: '13px', color: theme.colors.textLight }}>
                        <strong>{rule.agent_pool?.length || 0}</strong> agent{(rule.agent_pool?.length || 0) !== 1 ? 's' : ''}
                        {rule.respect_capacity && ` • Max ${rule.max_active_leads} leads`}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => handleToggleActive(rule.id, rule.is_active)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: rule.is_active ? theme.colors.success : theme.colors.bg,
                          color: rule.is_active ? 'white' : theme.colors.textLight,
                          border: 'none',
                          borderRadius: theme.radius.sm,
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor = rule.is_active ? '#059669' : theme.colors.border)
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = rule.is_active ? theme.colors.success : theme.colors.bg)
                        }
                      >
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </button>
                      <button
                        onClick={() => {
                          setEditingRule(rule);
                          setShowEditor(true);
                        }}
                        style={{
                          padding: '6px 10px',
                          backgroundColor: 'transparent',
                          color: theme.colors.primary,
                          border: `1px solid ${theme.colors.border}`,
                          borderRadius: theme.radius.sm,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                        }}
                      >
                        <Edit2 size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteRule(rule.id)}
                        style={{
                          padding: '6px 10px',
                          backgroundColor: 'transparent',
                          color: theme.colors.danger,
                          border: `1px solid ${theme.colors.border}`,
                          borderRadius: theme.radius.sm,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        )
      ) : (
        /* Log View */
        <div
          style={{
            backgroundColor: theme.colors.card,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.radius.md,
            overflow: 'hidden',
          }}
        >
          {loadingLog ? (
            <div style={{ padding: '24px', textAlign: 'center', color: theme.colors.textLight }}>
              Loading routing log...
            </div>
          ) : log.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: theme.colors.textLight }}>
              No routing history yet.
            </div>
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px',
              }}
            >
              <thead>
                <tr style={{ backgroundColor: theme.colors.bg, borderBottom: `1px solid ${theme.colors.border}` }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.text }}>
                    Date
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.text }}>
                    Contact
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.text }}>
                    Rule
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.text }}>
                    Assigned To
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.text }}>
                    Method
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.text }}>
                    Claimed By
                  </th>
                </tr>
              </thead>
              <tbody>
                {log.map((entry) => (
                  <tr
                    key={entry.id}
                    style={{
                      borderBottom: `1px solid ${theme.colors.border}`,
                      backgroundColor: 'transparent',
                    }}
                  >
                    <td style={{ padding: '12px', color: theme.colors.textLight }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={14} />
                        {new Date(entry.created_at).toLocaleString()}
                      </div>
                    </td>
                    <td style={{ padding: '12px', color: theme.colors.text, fontWeight: '500' }}>
                      {entry.contact?.first_name} {entry.contact?.last_name}
                    </td>
                    <td style={{ padding: '12px', color: theme.colors.text }}>{entry.rule?.name}</td>
                    <td style={{ padding: '12px', color: theme.colors.text }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <User size={14} color={theme.colors.primary} />
                        {entry.assigned_to?.full_name}
                      </div>
                    </td>
                    <td style={{ padding: '12px', color: theme.colors.textLight }}>
                      {entry.method?.replace(/_/g, ' ')}
                    </td>
                    <td style={{ padding: '12px', color: theme.colors.text }}>
                      {entry.claimed_by?.full_name || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && (
        <RuleEditorModal
          rule={editingRule}
          agents={agents}
          onSave={handleSaveRule}
          onCancel={() => {
            setShowEditor(false);
            setEditingRule(null);
          }}
        />
      )}
    </div>
  );
}
